import { useQuery } from '@apollo/client';
import { Navigate, useParams } from 'react-router-dom';

import { Stack } from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import { GetPrescriberOrderDocument } from '../../../graphql/generated/graphql';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { Loader } from '../../common/Loader';
import { Prescriber } from '../../common/Prescriber';
import { PrescriptionCancelledAlert } from '../../common/PrescriptionCancelledAlert';
import PatientMedicationDetailsHeader from '../order-details/PatientMedicationDetailsHeader';
import PrescriberOrderDetails from '../order-details/PrescriberOrderDetails';

import { LastActivityCard } from './LastActivityCard';

const OrderSummaryPage = () => {
    const { prescriberOrderId } = useParams();

    const { data, loading } = useQuery(GetPrescriberOrderDocument, {
        variables: { prescriberOrderId: prescriberOrderId ?? '' },
        skip: !prescriberOrderId,
    });

    if (!prescriberOrderId) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    if (loading) {
        return <Loader />;
    }

    const prescriberOrder = getFragment(data?.prescriberOrderV2);

    if (!prescriberOrder) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    if (prescriberOrder.__typename !== 'DigitallyReceivedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.verifyPatient', {
                    prescriberOrderId,
                })}
            />
        );
    }

    const patient = getFragment(prescriberOrder.patient);
    const prescriber = getFragment(prescriberOrder.prescriber);

    return (
        <Stack>
            <PrescriptionCancelledAlert prescriberOrder={prescriberOrder} />
            <PatientMedicationDetailsHeader
                patientName={patient.fullName}
                prescriberOrder={prescriberOrder}
            />
            <PrescriberOrderDetails prescriberOrder={prescriberOrder} />
            <LastActivityCard prescriberOrder={prescriberOrder} />
            <Prescriber prescriber={prescriber} />
        </Stack>
    );
};

export default OrderSummaryPage;
